// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableCellHeader from "@dashboard/components/TableCellHeader";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  Chip,
  IconButton,
  Tab,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import SubdirectoryArrowRightIcon from "@material-ui/icons/SubdirectoryArrowRight";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import ConfigDialog from "../components/ConfigDialog";
import SectionDialog from "../components/SectionDialog";
import SduiPreviewDialog from "../components/SduiPreviewDialog";
import ThemeDialog from "../components/ThemeDialog";
import {
  SDUI_CONFIG_DELETE_MUTATION,
  SDUI_SECTION_DELETE_MUTATION,
  SDUI_SECTION_REORDER_MUTATION,
  SDUI_THEME_DELETE_MUTATION,
} from "../sduiMutations";
import {
  SDUI_CONFIGS_QUERY,
  SDUI_SECTIONS_QUERY,
  SDUI_THEMES_QUERY,
  STOREFRONT_LAYOUT_QUERY,
} from "../sduiQueries";
import { SDUIConfig, SDUISection, SDUITheme } from "../types";

interface DeleteTarget {
  id: string;
  name: string;
  type: "Visual Theme" | "Dynamic Section" | "Remote Config Parameter";
}

export const SduiPage: React.FC = () => {
  const notify = useNotifier();
  const [tabIndex, setTabIndex] = useState<number>(0);

  // Dialog states
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<SDUITheme | null>(null);

  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<SDUISection | null>(null);

  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SDUIConfig | null>(null);

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewThemeId, setPreviewThemeId] = useState<string>("");

  // Delete Confirmation state
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  // Queries
  const { data: themesData, loading: themesLoading } = useQuery(SDUI_THEMES_QUERY, {
    fetchPolicy: "cache-and-network",
  });
  const { data: sectionsData, loading: sectionsLoading } = useQuery(SDUI_SECTIONS_QUERY, {
    fetchPolicy: "cache-and-network",
  });
  const { data: configsData, loading: configsLoading } = useQuery(SDUI_CONFIGS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  // Deletions
  const [deleteTheme, { loading: deletingTheme }] = useMutation(SDUI_THEME_DELETE_MUTATION, {
    refetchQueries: [{ query: SDUI_THEMES_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Theme deleted" });
      setDeleteTarget(null);
    },
  });
  const [deleteSection, { loading: deletingSection }] = useMutation(SDUI_SECTION_DELETE_MUTATION, {
    refetchQueries: [{ query: SDUI_SECTIONS_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Section deleted" });
      setDeleteTarget(null);
    },
  });
  const [deleteConfig, { loading: deletingConfig }] = useMutation(SDUI_CONFIG_DELETE_MUTATION, {
    refetchQueries: [{ query: SDUI_CONFIGS_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Remote config parameter deleted" });
      setDeleteTarget(null);
    },
  });

  // Reorder Sections
  const [reorderSections, { loading: reorderingSections }] = useMutation(SDUI_SECTION_REORDER_MUTATION, {
    refetchQueries: [
      { query: SDUI_SECTIONS_QUERY },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "APP" } },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "WEB" } },
    ],
    onCompleted: data => {
      if (data.sduiSectionReorder?.errors?.length) {
        notify({ status: "error", text: data.sduiSectionReorder.errors[0].message });
      } else {
        notify({ status: "success", text: "Homepage section order updated" });
      }
    },
  });

  const handleMoveSection = (currentIndex: number, direction: "up" | "down") => {
    const list = [...(sectionsData?.sduiSections || [])];
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= list.length) return;

    // Swap positions
    const [moved] = list.splice(currentIndex, 1);
    list.splice(targetIndex, 0, moved);

    const ids = list.map((s: SDUISection) => s.id);
    reorderSections({ variables: { ids } });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    const { id, type } = deleteTarget;
    if (type === "Visual Theme") deleteTheme({ variables: { id } });
    else if (type === "Dynamic Section") deleteSection({ variables: { id } });
    else if (type === "Remote Config Parameter") deleteConfig({ variables: { id } });
  };

  const getPrimaryAction = () => {
    switch (tabIndex) {
      case 0:
        return (
          <Button
            onClick={() => {
              setEditingTheme(null);
              setThemeDialogOpen(true);
            }}
          >
            Create Theme
          </Button>
        );
      case 1:
        return (
          <Button
            onClick={() => {
              setEditingSection(null);
              setSectionDialogOpen(true);
            }}
          >
            Add Homepage Section
          </Button>
        );
      case 2:
        return (
          <Button
            onClick={() => {
              setEditingConfig(null);
              setConfigDialogOpen(true);
            }}
          >
            Add Remote Config
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <ListPageLayout>
      <TopNav title="Server-Driven UI & Dynamic Theming">
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="secondary"
            onClick={() => {
              setPreviewThemeId("");
              setPreviewDialogOpen(true);
            }}
            style={{ display: "flex", gap: 6, alignItems: "center" }}
          >
            <VisibilityIcon fontSize="small" /> Live App & Web Preview
          </Button>
          {getPrimaryAction()}
        </Box>
      </TopNav>

      <Box display="flex" flexDirection="column" gap={4}>
        <DashboardCard>
          <Tabs
            value={tabIndex}
            onChange={(_, val) => setTabIndex(val)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Visual Themes & Seasonal Styling" />
            <Tab label="Dynamic Homepage Sections (Tree)" />
            <Tab label="Remote Config & Feature Flags" />
          </Tabs>
        </DashboardCard>

        {/* ── TAB 0: THEMES ── */}
        {tabIndex === 0 && (
          <DashboardCard>
            {themesLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Theme Key</TableCellHeader>
                    <TableCellHeader>Label</TableCellHeader>
                    <TableCellHeader>Priority</TableCellHeader>
                    <TableCellHeader>Palette Preview</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 110 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(themesData?.sduiThemes || []).map((t: SDUITheme) => {
                    let parsed: any = {};
                    try {
                      parsed = typeof t.themeData === "string" ? JSON.parse(t.themeData) : t.themeData;
                    } catch {}
                    return (
                      <TableRow key={t.id} hover>
                        <TableCell>
                          <Typography variant="body2" style={{ fontFamily: "monospace", fontWeight: 700 }}>
                            {t.name}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="subtitle2">{t.label}</Typography></TableCell>
                        <TableCell>{t.priority}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={1} alignItems="center">
                            {parsed.primaryColor && (
                              <div
                                title={`Primary: ${parsed.primaryColor}`}
                                style={{ width: 20, height: 20, borderRadius: "50%", backgroundColor: parsed.primaryColor, border: "1px solid #ddd" }}
                              />
                            )}
                            {parsed.secondaryColor && (
                              <div
                                title={`Secondary: ${parsed.secondaryColor}`}
                                style={{ width: 20, height: 20, borderRadius: "50%", backgroundColor: parsed.secondaryColor, border: "1px solid #ddd" }}
                              />
                            )}
                            {parsed.accentColor && (
                              <div
                                title={`Accent: ${parsed.accentColor}`}
                                style={{ width: 20, height: 20, borderRadius: "50%", backgroundColor: parsed.accentColor, border: "1px solid #ddd" }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={t.isPublished ? "Active" : "Draft"}
                            style={{
                              backgroundColor: t.isPublished ? "#dcfce7" : "#f1f5f9",
                              color: t.isPublished ? "#15803d" : "#475569",
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" style={{ width: 140 }}>
                          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                            <IconButton
                              size="small"
                              title={`Preview ${t.label || t.name}`}
                              onClick={() => {
                                setPreviewThemeId(t.id);
                                setPreviewDialogOpen(true);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingTheme(t);
                                setThemeDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteTarget({ id: t.id, name: t.label || t.name, type: "Visual Theme" })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!themesData?.sduiThemes || themesData.sduiThemes.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box padding={4}><Text>No themes defined yet.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 1: SECTIONS TREE ── */}
        {tabIndex === 1 && (
          <DashboardCard>
            {sectionsLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Section Key</TableCellHeader>
                    <TableCellHeader>Label</TableCellHeader>
                    <TableCellHeader>Component Type</TableCellHeader>
                    <TableCellHeader>Order</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 170 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(sectionsData?.sduiSections || []).map((sec: SDUISection, index: number) => {
                    const totalSections = (sectionsData?.sduiSections || []).length;
                    return (
                    <React.Fragment key={sec.id}>
                      {/* Top-Level Section Row */}
                      <TableRow hover style={{ backgroundColor: sec.sectionType === "FEATURED_GROUP" ? "#f8fafc" : "inherit" }}>
                        <TableCell>
                          <Typography variant="body2" style={{ fontFamily: "monospace", fontWeight: 700 }}>
                            {sec.sectionKey}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">{sec.label}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={sec.sectionType}
                            style={{
                              backgroundColor: sec.sectionType === "FEATURED_GROUP" ? "#f3e8ff" : "#e0f2fe",
                              color: sec.sectionType === "FEATURED_GROUP" ? "#7e22ce" : "#0369a1",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>{sec.sortOrder}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={sec.isPublished ? "Rendered" : "Hidden"}
                            style={{
                              backgroundColor: sec.isPublished ? "#dcfce7" : "#fee2e2",
                              color: sec.isPublished ? "#15803d" : "#b91c1c",
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" style={{ width: 170 }}>
                          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                            <IconButton
                              size="small"
                              title="Move section up"
                              disabled={index === 0 || reorderingSections}
                              onClick={() => handleMoveSection(index, "up")}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Move section down"
                              disabled={index === totalSections - 1 || reorderingSections}
                              onClick={() => handleMoveSection(index, "down")}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Edit section"
                              onClick={() => {
                                setEditingSection(sec);
                                setSectionDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Delete section"
                              onClick={() => setDeleteTarget({ id: sec.id, name: sec.label, type: "Dynamic Section" })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Nested Sub-sections if any */}
                      {(sec.subsections || []).map((sub: SDUISection) => (
                        <TableRow key={sub.id} hover style={{ backgroundColor: "#fdfefe" }}>
                          <TableCell style={{ paddingLeft: 40 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <SubdirectoryArrowRightIcon color="action" fontSize="small" />
                              <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                                {sub.sectionKey}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell style={{ paddingLeft: 40 }}>
                            <Typography variant="body2">{sub.label}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" label={sub.sectionType} />
                          </TableCell>
                          <TableCell>{sub.sortOrder}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={sub.isPublished ? "Rendered" : "Hidden"}
                              style={{
                                backgroundColor: sub.isPublished ? "#dcfce7" : "#fee2e2",
                                color: sub.isPublished ? "#15803d" : "#b91c1c",
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" style={{ width: 110 }}>
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingSection(sub);
                                  setSectionDialogOpen(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setDeleteTarget({ id: sub.id, name: sub.label, type: "Dynamic Section" })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
                  {(!sectionsData?.sduiSections || sectionsData.sduiSections.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box padding={4}><Text>No homepage sections created.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 2: REMOTE CONFIG ── */}
        {tabIndex === 2 && (
          <DashboardCard>
            {configsLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Key</TableCellHeader>
                    <TableCellHeader>Label</TableCellHeader>
                    <TableCellHeader>Platform</TableCellHeader>
                    <TableCellHeader>Priority</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 110 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(configsData?.sduiConfigs || []).map((c: SDUIConfig) => (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontFamily: "monospace", fontWeight: 700 }}>
                          {c.key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{c.label}</Typography>
                        {c.description && (
                          <Typography variant="caption" color="textSecondary">{c.description}</Typography>
                        )}
                      </TableCell>
                      <TableCell><Chip size="small" label={c.platform} /></TableCell>
                      <TableCell>{c.priority}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={c.isPublished ? "Active" : "Disabled"}
                          style={{
                            backgroundColor: c.isPublished ? "#dcfce7" : "#fee2e2",
                            color: c.isPublished ? "#15803d" : "#b91c1c",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" style={{ width: 110 }}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingConfig(c);
                              setConfigDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setDeleteTarget({
                                id: c.id,
                                name: `${c.label} (${c.key})`,
                                type: "Remote Config Parameter",
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!configsData?.sduiConfigs || configsData.sduiConfigs.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box padding={4}><Text>No remote configs configured.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}
      </Box>

      {/* Modals */}
      <ThemeDialog
        key={editingTheme?.id || "new-theme"}
        open={themeDialogOpen}
        theme={editingTheme}
        onClose={() => {
          setThemeDialogOpen(false);
          setEditingTheme(null);
        }}
      />
      <SectionDialog
        key={editingSection?.id || "new-section"}
        open={sectionDialogOpen}
        section={editingSection}
        onClose={() => {
          setSectionDialogOpen(false);
          setEditingSection(null);
        }}
      />
      <ConfigDialog
        key={editingConfig?.id || "new-config"}
        open={configDialogOpen}
        config={editingConfig}
        onClose={() => {
          setConfigDialogOpen(false);
          setEditingConfig(null);
        }}
      />

      {/* Delete Confirmation Popup */}
      <ActionDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.type || "Item"}?`}
        variant="delete"
        confirmButtonLabel="Delete"
        confirmButtonState={
          deletingTheme || deletingSection || deletingConfig
            ? "loading"
            : "default"
        }
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      >
        <Text>
          Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
        </Text>
      </ActionDialog>

      {/* Live Storefront & Mobile App Preview Modal */}
      <SduiPreviewDialog
        open={previewDialogOpen}
        initialThemeId={previewThemeId}
        onClose={() => setPreviewDialogOpen(false)}
        themes={themesData?.sduiThemes || []}
      />
    </ListPageLayout>
  );
};

export default SduiPage;
